import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from 'react';
import { useLocation } from 'wouter';
import { chrome, normalizeRoute, portfolioItems, type NavItem } from '@/lib/content';
import { STICKY_HEADER } from '@/lib/switches';
import { scrollToContactBand } from '@/lib/contact-scroll';
import { UI } from '@/lib/ui-strings';
import { ChevronDownIcon, CloseIcon, MenuIcon } from './Icons';
import SmartLink from './SmartLink';
import { getMedicalOfficeRoute } from '@/lib/medical-family-office';
import { GERMANY_NAV, isGermanyPath, SWISS_ORIGIN } from '@/lib/markets';
import { useMarket } from '@/lib/market-context';
import CountrySwitcher from './CountrySwitcher';
import { OfficeLanguageSwitcher } from './MedicalOfficeChrome';

/**
 * Site header. design.md 5.1.
 *
 * Two tiers at `lg` and up: a 36px utility row that carries the promoted
 * Datensicherheit surface, and a 72px main bar that stays pinned. Below `lg` a
 * single 64px bar and a drawer.
 *
 * Whether the bar is pinned at all is `STICKY_HEADER` in `src/lib/switches.ts`:
 * six of the twelve headers the benchmark study measured scroll away, and the
 * client has not decided. `--header-h` drives scroll-margin either way.
 */

function isActive(current: string, href: string): boolean {
  const a = normalizeRoute(current);
  const b = normalizeRoute(href);
  return a === b || (b !== '/' && a.startsWith(b + '/'));
}

/** The utility row: three links, taken from the database's own menus. */
function utilityItems(): NavItem[] {
  const all: NavItem[] = [
    ...(chrome.nav.utility as NavItem[]),
    ...(chrome.nav.mobileExtra as NavItem[]),
    ...(chrome.nav.primary as NavItem[]),
  ];
  const order = ['/datensicherheit', '/referenzen', '/ueber-uns', '/news'];
  const picked: NavItem[] = [];
  for (const href of order) {
    const found = all.find((item) => normalizeRoute(item.href) === href);
    if (found && !picked.some((p) => p.href === found.href)) picked.push(found);
  }
  return picked;
}

/** The two navigation items that open a panel, built from the routed content. */
function submenuFor(href: string): NavItem[] | null {
  const route = normalizeRoute(href);
  if (route === '/executive-medical-office') {
    return [
      { label: 'Executive Medical Office (für Unternehmen)', href: '/executive-medical-office' },
      { label: 'Private Medical Office (für Privatpersonen)', href: '/private-medical-office' },
    ];
  }
  if (route === '/screenings') {
    return [
      ...portfolioItems.map((p) => ({ label: p.title, href: p.route })),
      { label: UI.navAllScreenings, href: '/screenings' },
    ];
  }
  if (route === '/genetic-sciences') {
    return [
      { label: 'Epigenetic Coaching', href: '/epigenetic-coaching' },
      { label: 'Pharmakogenetik', href: '/pharmakogenetik' },
      { label: UI.navOverviewGenetic, href: '/genetic-sciences' },
    ];
  }
  if (route === '/bgm-zahlen-schweiz') {
    return [
      { label: 'BGM-Kennzahlen Schweiz 2026', href: '/bgm-zahlen-schweiz' },
      { label: 'Business Case BGM', href: '/business-case-bgm' },
      { label: 'Gesundheitsprogramm einführen', href: '/bgm-einfuehren' },
      { label: 'Datenschutz im BGM', href: '/datenschutz-bgm' },
    ];
  }
  return null;
}

function NavItemLink({
  item,
  current,
  className,
  onNavigate,
  children,
}: {
  item: NavItem;
  current: string;
  className: string;
  onNavigate?: () => void;
  children?: ReactNode;
}) {
  const active = isActive(current, item.href);
  return (
    <SmartLink
      to={item.href}
      className={`${className}__link`}
      data-active={active ? 'true' : 'false'}
      {...(active ? { 'aria-current': 'page' as const } : null)}
      onClick={onNavigate}
    >
      {children ?? item.label}
    </SmartLink>
  );
}

/** One primary navigation item, with its dropdown panel when it has one. */
function PrimaryItem({
  item,
  current,
  openId,
  setOpenId,
}: {
  item: NavItem;
  current: string;
  openId: string | null;
  setOpenId: Dispatch<SetStateAction<string | null>>;
}) {
  const submenu = useMemo(() => submenuFor(item.href), [item.href]);
  const id = useId();
  const open = openId === id;
  const hoverTimer = useRef<number | undefined>(undefined);
  const itemRef = useRef<HTMLLIElement | null>(null);

  const clearTimer = () => {
    if (hoverTimer.current) window.clearTimeout(hoverTimer.current);
    hoverTimer.current = undefined;
  };

  useEffect(() => clearTimer, []);

  if (!submenu) {
    return (
      <li className="hp-nav__item">
        <NavItemLink item={item} current={current} className="hp-nav" />
      </li>
    );
  }

  return (
    <li
      ref={itemRef}
      className="hp-nav__item hp-nav__item--menu"
      data-open={open ? 'true' : 'false'}
      onPointerEnter={(event) => {
        if (event.pointerType === 'touch') return;
        clearTimer();
        setOpenId(id);
      }}
      onPointerLeave={(event) => {
        if (event.pointerType === 'touch') return;
        clearTimer();
        hoverTimer.current = window.setTimeout(() => {
          // Layout/focus transitions can emit leave without the pointer
          // actually leaving this item or its panel. Check the live hit area.
          if (itemRef.current?.matches(':hover')) return;
          // A previous menu's delayed leave must not close the next menu.
          setOpenId((currentId) => currentId === id ? null : currentId);
        }, 200);
      }}
      onBlur={(event) => {
        if (
          !event.currentTarget.contains(event.relatedTarget as Node | null) &&
          !event.currentTarget.matches(':hover')
        ) {
          setOpenId((currentId) => currentId === id ? null : currentId);
        }
      }}
    >
      <NavItemLink item={item} current={current} className="hp-nav" />
      <button
        type="button"
        className="hp-nav__disclosure"
        aria-expanded={open}
        aria-controls={id}
        aria-label={UI.navSubmenu(item.label)}
        onClick={() => {
          clearTimer();
          setOpenId((currentId) => currentId === id ? null : id);
        }}
      >
        <ChevronDownIcon className="hp-nav__chevron" width={16} height={16} />
      </button>
      <div className="hp-menu" id={id} hidden={!open}>
        <ul className="hp-menu__list">
          {submenu.map((child) => (
            <li className="hp-menu__item" key={child.href + child.label}>
              <SmartLink
                to={child.href}
                className="hp-menu__link"
                data-active={isActive(current, child.href) ? 'true' : 'false'}
                onClick={() => setOpenId(null)}
              >
                {child.label}
              </SmartLink>
            </li>
          ))}
        </ul>
      </div>
    </li>
  );
}

export default function Header() {
  const [location] = useLocation();
  const officePage = getMedicalOfficeRoute(location);
  const market = useMarket();
  const germany = isGermanyPath(location);
  const [open, setOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [scrolled, setScrolled] = useState(false);
  const [drawerSubmenu, setDrawerSubmenu] = useState<string | null>(null);
  const drawerId = useId();
  const toggleRef = useRef<HTMLButtonElement | null>(null);
  const drawerRef = useRef<HTMLDivElement | null>(null);
  const headerRef = useRef<HTMLElement | null>(null);

  const close = useCallback(() => setOpen(false), []);

  // Measure actual item widths: retain a prefix of the menu at every viewport.
  useEffect(() => {
    const inner = headerRef.current?.querySelector<HTMLElement>('.hp-header__inner');
    const nav = inner?.querySelector<HTMLElement>('.hp-nav');
    const list = nav?.querySelector<HTMLElement>('.hp-nav__list');
    if (!inner || !nav || !list) return;
    let disposed = false;
    const fit = () => {
      if (disposed) return;
      const items = Array.from(list.children) as HTMLElement[];
      items.forEach(item => { item.hidden = false; });
      const gap = parseFloat(getComputedStyle(list).columnGap) || 0;
      let used = 0;
      let overflow = false;
      items.forEach((item, index) => {
        used += item.getBoundingClientRect().width + (index ? gap : 0);
        overflow ||= used > nav.clientWidth;
        item.hidden = overflow;
      });
    };
    const observer = new ResizeObserver(fit);
    observer.observe(inner);
    fit();
    void document.fonts.ready.then(fit);
    return () => { disposed = true; observer.disconnect(); };
  }, []);

  // A navigation always closes the drawer and any open panel.
  useEffect(() => {
    setOpen(false);
    setOpenMenu(null);
    setDrawerSubmenu(null);
  }, [location]);

  useEffect(() => {
    const root = document.documentElement;
    if (open) root.setAttribute('data-menu-open', 'true');
    else root.removeAttribute('data-menu-open');
    return () => root.removeAttribute('data-menu-open');
  }, [open]);

  // The main bar's bottom hairline appears after 8px of scroll (5.1).
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // A dropdown closes on Escape and on a click outside the header.
  useEffect(() => {
    if (!openMenu) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpenMenu(null);
    };
    const onDown = (event: MouseEvent) => {
      if (!headerRef.current?.contains(event.target as Node)) setOpenMenu(null);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onDown);
    };
  }, [openMenu]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        close();
        toggleRef.current?.focus();
        return;
      }
      if (event.key !== 'Tab') return;
      const drawer = drawerRef.current;
      if (!drawer) return;
      const focusable = drawer.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const activeEl = document.activeElement;
      if (event.shiftKey && (activeEl === first || activeEl === toggleRef.current)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && activeEl === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, close]);

  const primary = germany ? GERMANY_NAV : chrome.nav.primary as NavItem[];
  const utilityRow = useMemo(() => germany
    ? [
      { label: 'Schweiz', href: SWISS_ORIGIN + '/' },
      { label: 'Referenzen', href: SWISS_ORIGIN + '/referenzen' },
      { label: 'Über Uns', href: SWISS_ORIGIN + '/ueber-uns' },
    ] : utilityItems(), [germany]);
  const drawerGroups = useMemo<{ title: string; items: (NavItem & { children?: NavItem[] })[] }[]>(() => germany ? [
    { title: 'Deutschland', items: [{ label: 'Übersicht', href: '/de-de' }, ...GERMANY_NAV] },
    { title: 'Health Prime AG', items: [
      { label: 'Schweizer Website', href: SWISS_ORIGIN + '/' },
      { label: 'Über Uns', href: SWISS_ORIGIN + '/ueber-uns' },
    ] },
  ] : [
    {
      title: 'Leistungen',
      items: [
        { label: 'Executive Medical Office', href: '/executive-medical-office' },
        { label: 'Private Medical Office', href: '/private-medical-office' },
        { label: 'Screenings', href: '/screenings', children: submenuFor('/screenings')!.filter((item) => item.href !== '/screenings') },
        { label: 'Workshops & Seminare', href: '/workshops-und-seminare-fuer-gesunde-mitarbeitende' },
        { label: 'Genetic Sciences', href: '/genetic-sciences', children: submenuFor('/genetic-sciences')!.filter((item) => item.href !== '/genetic-sciences') },
      ],
    },
    {
      title: 'Unternehmen',
      items: [
        { label: 'Referenzen', href: '/referenzen' },
        { label: 'Über Uns', href: '/ueber-uns' },
        { label: 'News', href: '/news' },
        { label: 'Datensicherheit', href: '/datensicherheit' },
      ],
    },
    {
      title: 'Facts & Figures',
      items: [
        ...submenuFor('/bgm-zahlen-schweiz')!,
        { label: 'Weitere Fakten zur betrieblichen Gesundheit', href: '/workplace-solutions' },
      ],
    },
  ], [germany]);

  return (
    <header
      className={`hp-header${STICKY_HEADER ? ' hp-header--sticky' : ''}`}
      data-open={open ? 'true' : 'false'}
      data-scrolled={scrolled ? 'true' : 'false'}
      ref={headerRef}
    >
      <div className="hp-header__utility">
        <div className="hp-container hp-header__utility-inner">
          <nav aria-label={UI.utilityNav}>
            <ul className="hp-utility__list">
              {utilityRow.map((item) => (
                <li className="hp-utility__item" key={item.href}>
                  <NavItemLink item={item} current={location} className="hp-utility" />
                </li>
              ))}
            </ul>
          </nav>
          <OfficeLanguageSwitcher page={officePage} />
        </div>
      </div>

      <div className="hp-header__bar">
        <div className="hp-container hp-header__inner">
          <SmartLink to={germany ? '/de-de' : '/'} className="hp-header__brand" aria-label={UI.toHome}>
            <img
              className="hp-header__logo"
              src={chrome.brand.logo}
              alt={chrome.brand.name}
              width={778}
              height={124}
              decoding="async"
            />
          </SmartLink>

          <nav className="hp-nav" aria-label={UI.mainNav}>
            <ul className="hp-nav__list">
              {primary.map((item) => (
                <PrimaryItem
                  item={item}
                  current={location}
                  openId={openMenu}
                  setOpenId={setOpenMenu}
                  key={item.href}
                />
              ))}
            </ul>
          </nav>

          <div className="hp-header__actions">
            <a
              className="hp-button hp-button--primary hp-button--sm hp-header__cta"
              href={market.germany ? '/de-de#kontakt-aufnehmen' : '/#kontakt-aufnehmen'}
              onClick={scrollToContactBand}
            >
              {UI.contact}
            </a>
            <button
              type="button"
              className="hp-header__toggle"
              aria-expanded={open}
              aria-controls={drawerId}
              aria-label={open ? UI.menuClose : UI.menuOpen}
              onClick={() => setOpen((v) => !v)}
              ref={toggleRef}
            >
              {open ? <CloseIcon /> : <MenuIcon />}
            </button>
          </div>
        </div>
      </div>

      <div
        className="hp-drawer-scrim"
        hidden={!open}
        onClick={close}
        aria-hidden="true"
        data-open={open ? 'true' : 'false'}
      />

      <div
        className="hp-drawer"
        id={drawerId}
        ref={drawerRef}
        hidden={!open}
        data-open={open ? 'true' : 'false'}
      >
        <div className="hp-drawer__inner">
          <div className="hp-drawer__heading">
            <span>Menü</span>
            <button type="button" className="hp-drawer__close" onClick={close} aria-label="Menü schliessen">
              <CloseIcon />
            </button>
          </div>
          <p className="hp-drawer__cta">
            {officePage ? (
              <a className="hp-button hp-button--primary" href="#kontakt" onClick={close}>Erstgespräch anfragen</a>
            ) : (
            <a
              className="hp-button hp-button--primary hp-drawer__whatsapp"
              href={chrome.contact.whatsapp}
              rel="noopener noreferrer"
              target="_blank"
            >
              WhatsApp
            </a>
            )}
          </p>

          <nav className="hp-drawer__nav" aria-label={UI.mobileNav}>
            {drawerGroups.map((group, groupIndex) => (
            <section className="hp-drawer__group" key={group.title} aria-labelledby={`${drawerId}-group-${groupIndex}`}>
            <h2 className="hp-drawer__group-title" id={`${drawerId}-group-${groupIndex}`}>{group.title}</h2>
            <ul className="hp-drawer__list">
              {group.items.map((item, itemIndex) => {
                const expanded = drawerSubmenu === item.href;
                const submenuId = `${drawerId}-sub-${groupIndex}-${itemIndex}`;
                return (
                  <li className="hp-drawer__item" key={item.href}>
                    <div className="hp-drawer__row">
                      <NavItemLink
                        item={item}
                        current={location}
                        className="hp-drawer"
                        onNavigate={close}
                      />
                      {item.children ? (
                        <button
                          type="button"
                          className="hp-drawer__disclosure"
                          aria-expanded={expanded}
                          aria-controls={submenuId}
                          aria-label={UI.navSubmenu(item.label)}
                          onClick={() => setDrawerSubmenu((value) => value === item.href ? null : item.href)}
                        >
                          <ChevronDownIcon width={20} height={20} />
                        </button>
                      ) : null}
                    </div>
                    {item.children ? (
                      <ul className="hp-drawer__sublist" id={submenuId} hidden={!expanded}>
                        {item.children.map((child) => (
                          <li className="hp-drawer__subitem" key={child.href}>
                            <SmartLink
                              to={child.href}
                              className="hp-drawer__sublink"
                              onClick={close}
                            >
                              {child.label}
                            </SmartLink>
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </li>
                );
              })}
            </ul>
            {group.title === 'Facts & Figures' ? <CountrySwitcher /> : null}
            </section>
            ))}
          </nav>

          <ul className="hp-drawer__legal">
            {(chrome.footer.legal as NavItem[]).map((item) => (
              <li key={item.href}>
                <SmartLink to={item.href} className="hp-drawer__legal-link" onClick={close}>
                  {item.label}
                </SmartLink>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </header>
  );
}
